import type { ReactNode } from 'react';
import { Collapse, Drawer } from 'antd';

import { Separator } from '../../../components/ui';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN } from '../../../lib/dinh-dang';
import { NGUON_GHI } from '../constants';
import type { SuKienAudit } from '../types';
import { NhanKetQua, useTenHanhDong } from './NhanSuKien';

function Dong({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-content-muted">{nhan}</dt>
      <dd className="mt-0.5 break-words text-sm text-content-primary">{children ?? KHONG_CO}</dd>
    </div>
  );
}

function Phan({ tieuDe, children }: { tieuDe: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-content-primary">{tieuDe}</h3>
      {children}
    </section>
  );
}

/** Giá trị trong `detail` ra một dòng chữ: boolean, object, rỗng. */
function inGiaTri(v: unknown, co: string, khong: string): string {
  if (v === null || v === undefined || v === '') return KHONG_CO;
  if (typeof v === 'boolean') return v ? co : khong;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

interface DongThayDoi {
  ten: string;
  truoc: unknown;
  sau: unknown;
}

/** `detail.changes` theo hai dạng đang ghi: `{field, …}` của SDK, `{key, …}` của cấu hình. */
function docThayDoi(detail: SuKienAudit['detail']): DongThayDoi[] {
  const ds = detail?.changes;
  if (!Array.isArray(ds)) return [];
  return ds.map((c: Record<string, unknown>) => ({
    ten: String(c.field ?? c.key ?? ''),
    truoc: c.before,
    sau: c.after,
  }));
}

/**
 * Ngăn chi tiết một thao tác (§8.2 mục 8 của đặc tả): tóm tắt, thay đổi, dữ
 * liệu kỹ thuật. Phần kỹ thuật **gập sẵn** — đa số người mở ngăn này cần biết
 * ai đã làm gì, không cần mã sự kiện.
 */
export function NganChiTiet({ suKien, onClose }: { suKien: SuKienAudit | null; onClose: () => void }) {
  const { t, locale } = useT();
  const tenHanhDong = useTenHanhDong();
  const thayDoi = docThayDoi(suKien?.detail ?? null);
  // Gateway và bộ lọc DENY ghi mã lỗi ở `error_code`, nguồn khác ghi `reason`.
  const lyDo = suKien?.detail?.reason ?? suKien?.detail?.error_code;

  return (
    <Drawer
      open={suKien !== null}
      onClose={onClose}
      title={t('nhatKy.chiTiet.tieuDe')}
      placement="right"
      width={640}
      destroyOnHidden
    >
      {suKien && (
        <div className="space-y-5">
          <Phan tieuDe={t('nhatKy.chiTiet.tomTat')}>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Dong nhan={t('nhatKy.cot.nguoi')}>
                {suKien.user_id ? (
                  <span className="font-mono text-xs">{suKien.user_id}</span>
                ) : (
                  t('nhatKy.heThong')
                )}
              </Dong>
              <Dong nhan={t('nhatKy.cot.thoiDiem')}>{ngayGioVN(suKien.created_at, locale)}</Dong>
              <Dong nhan={t('nhatKy.cot.thaoTac')}>{tenHanhDong(suKien.action)}</Dong>
              <Dong nhan={t('nhatKy.chiTiet.doiTuong')}>
                {suKien.resource && <span className="break-all font-mono text-xs">{suKien.resource}</span>}
              </Dong>
              <Dong nhan={t('nhatKy.cot.ketQua')}>
                <NhanKetQua quyetDinh={suKien.decision} />
              </Dong>
              <Dong nhan={t('nhatKy.chiTiet.lyDo')}>
                {typeof lyDo === 'string' ? <span className="font-mono text-xs">{lyDo}</span> : null}
              </Dong>
              <Dong nhan={t('nhatKy.chiTiet.ip')}>
                {suKien.ip && <span className="break-all font-mono text-xs">{suKien.ip}</span>}
              </Dong>
              <Dong nhan={t('nhatKy.chiTiet.nguonGhi')}>{suKien.source && t(NGUON_GHI[suKien.source])}</Dong>
            </dl>
          </Phan>

          <Separator />

          <Phan tieuDe={t('nhatKy.chiTiet.thayDoi')}>
            {thayDoi.length === 0 ? (
              <p className="text-xs text-content-muted">{t('nhatKy.chiTiet.khongDoi')}</p>
            ) : (
              <ul className="divide-y divide-default rounded border border-default">
                {thayDoi.map((c) => (
                  <li key={c.ten} className="space-y-1 px-3 py-2.5">
                    <p className="break-all font-mono text-xs text-content-secondary">{c.ten}</p>
                    <p className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="break-all text-content-muted line-through">
                        {inGiaTri(c.truoc, t('nhatKy.co'), t('nhatKy.khong'))}
                      </span>
                      <span aria-label={t('caiDat.thayDoi.thanh')} className="text-content-muted">
                        →
                      </span>
                      <span className="break-all font-medium text-warning-700">
                        {inGiaTri(c.sau, t('nhatKy.co'), t('nhatKy.khong'))}
                      </span>
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Phan>

          <Separator />

          <Collapse
            size="small"
            items={[
              {
                key: 'ky-thuat',
                label: t('nhatKy.chiTiet.duLieuKyThuat'),
                children: (
                  <div className="space-y-3">
                    <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Dong nhan={t('nhatKy.chiTiet.maSuKien')}>
                        {suKien.event_id && (
                          <span className="break-all font-mono text-xs">{suKien.event_id}</span>
                        )}
                      </Dong>
                      <Dong nhan={t('nhatKy.chiTiet.maBanGhi')}>
                        <span className="break-all font-mono text-xs">{suKien.id}</span>
                      </Dong>
                    </dl>
                    <pre className="max-h-80 overflow-auto rounded border border-default bg-subtle p-3 font-mono text-xs text-content-secondary">
                      {JSON.stringify(suKien.detail ?? {}, null, 2)}
                    </pre>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
    </Drawer>
  );
}
